/**
 * #453 — operator-facing copy for the PER-MESSAGE READ RECEIPT on the #420a community
 * coordination thread. Pinned by communityCommsVocabulary.lock.test.ts (presence + forbidden +
 * honesty + reuse-consistency).
 *
 * DOCTRINE (CTO-ratified 2026-07-06, 3-lens opus pre-gate; the shipped receipt family):
 *   • EXPLICIT acknowledge only — a receipt exists ONLY for a real in-app acknowledge (never
 *     auto-on-render — surveillance-adjacent; the COP's only shipped receipt precedent, the
 *     interops / PEOC-Arrivals surfaces, is explicit). So the action is "Acknowledge" and the
 *     post-ack state is "Acknowledged · HH:MM" — NEVER "Read" / "Delivered" / "Check-in"
 *     (App A #9 Check-in collision + the sent≠read honesty locks; Ontario IMS §2.2 / council A.6).
 *   • The receipt attaches to a MESSAGE and names an OPERATOR SEAT — never a community being
 *     watched (FN-dignity, standing). No string glosses a receipt as watching / surveilling a
 *     community party.
 *   • The "Acknowledged · HH:MM" family REUSES the shipped receipt wording verbatim
 *     (interopsVocabulary.acknowledgedAtLabel / ACKNOWLEDGED_LABEL) — the lock test cross-checks
 *     them so the family cannot drift.
 *
 * Kept in its own module (no React import) so the pinned vocabulary lock can scan every
 * operator string without importing the component tree. Same idiom as interopsVocabulary.ts +
 * peocArrivalsVocabulary.ts.
 */

/* ── The receipt action (on a NON-mine message) ───────────────────────────── */

/** The explicit acknowledge affordance (records a receipt; never auto-fired on render). */
export const ACKNOWLEDGE_LABEL = 'Acknowledge';
/** In-flight state while the acknowledge POST is pending. */
export const ACKNOWLEDGING_LABEL = 'Acknowledging…';

/* ── The post-ack state (the shipped "Acknowledged · HH:MM" family) ───────── */

/** The bare receipt word — byte-identical to the shipped family (locked cross-check). */
export const ACKNOWLEDGED_LABEL = 'Acknowledged';

/** The VIEWER's own receipt on a message they acknowledged: "Acknowledged · HH:MM". */
export function acknowledgedAtLabel(hhmm: string): string {
  return `${ACKNOWLEDGED_LABEL} · ${hhmm}`;
}

/** The AUTHOR's view of who acknowledged their message: "Acknowledged by <seat> · HH:MM". The
 *  <seat> is an operator seat (PEOC duty officer / Municipal EOC) — NEVER a community. */
export function acknowledgedByLabel(seat: string, hhmm: string): string {
  return `${ACKNOWLEDGED_LABEL} by ${seat} · ${hhmm}`;
}

/* ── #420a thread framing (shared with the #454 coordination inbox) ───────── */

/** The always-on disclaimer that this is an INTERNAL operator coordination log — NOT a
 *  resident-facing bulletin (the resident push is #420b). Extracted here so the #454
 *  multi-community coordination inbox reuses the SAME framing verbatim (drift-proof): the
 *  coordination register attaches to the operator's WORK, never to watching a community. */
export const OPERATOR_COORDINATION_DISCLAIMER = 'Operator coordination — not resident-facing.';

/** Names the bound community on the coordination WORK: "Coordinating on <community>." The
 *  register attaches to the thread/work — never a surveillance framing of the community. */
export function coordinatingOnLabel(community: string): string {
  return `Coordinating on ${community}.`;
}

/* ── Hazard provenance (migration 0154, §G rebuild Wave 2) ─────────────────── */

/**
 * The provenance framing on a message posted from a hazard (the OC-fire panel): the §4.2
 * communication cycle names WHICH fire it is relaying about, so the record "stands up in an
 * after-action" (the First Nation seat's ruling, §G gate 2026-07-13). FACT-ONLY, by construction:
 * these strings carry NO recommendation, NO score, NO adjective, NO all-clear — the fire-alert
 * feature's ONLY doctrinally-seated verb is relay + inform (Ontario IMS §4.2), never a verdict.
 * Pinned by communityCommsVocabulary.lock.test.ts (doctrine-forbidden scan below).
 */

/** The reference PREFIX before a bound hazard: "re:". A relay reference, never a recommendation. */
export const HAZARD_PROVENANCE_PREFIX = 're:';

/** The operator-facing NAME of a bound hazard, derived from (source_type, human ref). For a fire:
 *  "Reported active wildland fire <number>" — the shipped record-type label (SidePanel), with the
 *  fire NUMBER when the source resolved it, else the bare type (the durable link is the stored id).
 *  No adjective, no score, no verdict. */
export function hazardName(sourceType: string, sourceRef: string | null | undefined): string {
  const ref = sourceRef ? ` ${sourceRef}` : '';
  switch (sourceType) {
    case 'active_wildland_fire':
      return `Reported active wildland fire${ref}`;
    default:
      return `Linked hazard${ref}`;
  }
}

/** The full provenance tag on a hazard-linked message / inbox row: "re: Reported active wildland
 *  fire K20524" — names WHICH fire the coordination is about. */
export function hazardProvenanceLabel(
  sourceType: string,
  sourceRef: string | null | undefined,
): string {
  return `${HAZARD_PROVENANCE_PREFIX} ${hazardName(sourceType, sourceRef)}`;
}

/** The thread's bound-hazard header when coordination is mounted FROM a hazard (the OC-fire panel):
 *  names the hazard (+ optional live facts) so the operator knows what this cycle is about. The
 *  optional context is FACT-ONLY enrichment (area / distance / access), never a conclusion. */
export function coordinatingAboutHazardLabel(hazardLabel: string, context?: string): string {
  return `${hazardLabel}${context ? ` · ${context}` : ''}`;
}

/* ── Honesty note (pinned verbatim) ───────────────────────────────────────── */

/** The always-available honesty note: a receipt exists ONLY for a real, explicit in-app
 *  acknowledge — never auto-on-render, never inferred. Deliberately token-clean (it carries
 *  none of "read" / "delivered" / "check-in" / "approve"), so it survives its own lock scan. */
export const COMMUNITY_RECEIPT_HONESTY_NOTE =
  'A receipt appears only when an operator has acknowledged this message in the COP — it is never inferred, and never shown just because the message was opened on screen.';
